import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  BookOpenCheck,
  UserCheck,
  Sparkles,
  CalendarClock,
  Video,
  FolderOpen,
  Users,
  Quote,
  Star,
  ArrowRight,
  Check,
  Globe2,
  HeartHandshake,
  GraduationCap,
} from "lucide-react";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import StickyCTA from "@/components/site/StickyCTA";
import CountdownTimer from "@/components/site/CountdownTimer";
import WaitingListForm from "@/components/site/WaitingListForm";
import TrialForm from "@/components/site/TrialForm";

const CONTACT_EMAIL = "hello@afrolingo.co.uk";
const WHATSAPP_NUMBER = "447000000000";

const TRUST = [
  { icon: UserCheck, label: "Qualified Native Teacher" },
  { icon: Sparkles, label: "Interactive Learning" },
  { icon: CalendarClock, label: "Weekly Live Classes" },
  { icon: Video, label: "Recorded Lessons" },
  { icon: FolderOpen, label: "Digital Materials" },
  { icon: Users, label: "Family Discounts" },
];

const CURRICULUM_BLOCKS = [
  {
    id: "block-1",
    title: "Foundations of Hausa",
    subtitle: "Weeks 1–4 · New skills, then first progress check",
    weeks: [
      {
        week: "Week 1",
        title: "Welcome to Hausa",
        theme: "Greetings & the Hausa Alphabet",
        detail:
          "Students are introduced to the Hausa language, learn common greetings and polite expressions, and begin recognising and pronouncing the Hausa alphabet. Confidence is built through listening and speaking activities.",
      },
      {
        week: "Week 2",
        title: "Numbers & Colours",
        theme: "Numbers 1–20 and Colours",
        detail:
          "Students learn to count from 1 to 20 and recognise common colours, then use both in simple classroom games and everyday conversations.",
      },
      {
        week: "Week 3",
        title: "My Family",
        theme: "Family Members & Relationships",
        detail:
          "Students learn vocabulary for immediate and extended family, describe simple relationships and practise introducing family members using basic Hausa sentences. Includes live teaching, interactive games, Q&A, homework and a digital workbook.",
      },
      {
        week: "Week 4",
        title: "Progress Assessment 1",
        theme: "Review & Beginner Assessment",
        assessment: true,
        detail:
          "Students review everything from Weeks 1–3 through fun quizzes, speaking activities, listening exercises and one-to-one conversations with Ameenah. Parents receive brief feedback on progress and areas for continued practice.",
      },
    ],
  },
  {
    id: "block-2",
    title: "Building Everyday Vocabulary",
    subtitle: "Weeks 5–8 · Real-world language, then second progress check",
    weeks: [
      {
        week: "Week 5",
        title: "Food & Everyday Life",
        theme: "Food, Drinks & Everyday Objects",
        detail:
          "Students learn common foods, drinks and household items while practising simple phrases such as asking for food, expressing likes and identifying everyday objects.",
      },
      {
        week: "Week 6",
        title: "Weather & Clothing",
        theme: "Weather, Seasons & Clothing",
        detail:
          "Students build vocabulary relating to the weather, seasons and clothing, enabling them to describe daily weather and what people are wearing.",
      },
      {
        week: "Week 7",
        title: "Around My Home",
        theme: "Home & Neighbourhood",
        detail:
          "Students learn vocabulary for rooms in the home, household items and places within their local community, practising simple descriptive sentences.",
      },
      {
        week: "Week 8",
        title: "Progress Assessment 2",
        theme: "Review & Practical Assessment",
        assessment: true,
        detail:
          "Students demonstrate what they have learned in Weeks 5–7 through vocabulary games, speaking exercises, listening activities and short conversational tasks. Individual feedback helps prepare students for more confident speaking.",
      },
    ],
  },
  {
    id: "block-3",
    title: "Speaking with Confidence",
    subtitle: "Weeks 9–12 · Real conversations, then celebration",
    weeks: [
      {
        week: "Week 9",
        title: "Everyday Conversations",
        theme: "Common Verbs, Simple Sentences & Role Play",
        detail:
          "Students learn frequently used verbs and begin forming complete sentences while participating in guided conversations and everyday role-play scenarios.",
      },
      {
        week: "Week 10",
        title: "Hausa Culture",
        theme: "Songs, Proverbs & Traditional Stories",
        detail:
          "Students discover the richness of Hausa culture through traditional songs, simple proverbs and storytelling, helping them appreciate the language beyond vocabulary and grammar.",
      },
      {
        week: "Week 11",
        title: "Real-Life Speaking Practice",
        theme: "Family Conversations",
        detail:
          "Students practise speaking confidently with parents, grandparents and relatives using everyday greetings, introductions and simple conversations — preparing learners to use Hausa naturally at home.",
      },
      {
        week: "Week 12",
        title: "Final Assessment & Celebration",
        theme: "End-of-Term Assessment, Certificates & Showcase",
        assessment: true,
        detail:
          "Students complete a final assessment covering the entire 12-week programme through speaking, listening and vocabulary activities. Families are invited to celebrate students' achievements as learners receive certificates of completion and showcase what they have learned.",
      },
    ],
  },
];

const FAQS = [
  {
    q: "Do I need to know any Hausa to start?",
    a: "Not at all. Our beginner courses assume zero prior knowledge. Every lesson starts from the basics and builds gradually.",
  },
  {
    q: "Are classes recorded?",
    a: "Yes — every live class is recorded and shared with members, so nobody misses a lesson.",
  },
  {
    q: "Should parents join?",
    a: "We encourage parents of younger children (5–8) to sit alongside their child. It makes learning warmer, and you'll pick up Hausa too.",
  },
  {
    q: "Can adults join?",
    a: "Absolutely. Our Saturday 10am class is designed for adults from complete beginner up to conversational level.",
  },
  {
    q: "What device do I need?",
    a: "Any laptop, tablet or phone with a camera and Google Meet. We'll email your link before each class.",
  },
  {
    q: "How does the family discount work?",
    a: "Siblings receive an automatic 30% discount on the monthly membership. Register them together on the waiting list.",
  },
];

const SCHEDULE = [
  { group: "Children · Ages 5–8", day: "Saturday", time: "10:00am UK", tone: "hsl(148 40% 92%)" },
  { group: "Children · Ages 9–13", day: "Saturday", time: "10:00am UK", tone: "hsl(41 70% 92%)" },
  { group: "Adults · All levels", day: "Saturday", time: "10:00am UK", tone: "hsl(15 55% 93%)" },
];

const HERO_IMG = "https://customer-assets-lxgj4vgw.emergentagent.net/job_heritage-hausa/artifacts/8k71v2v9_inatimi-nathus-UygW_kIv2A4-unsplash.webp";
const CHILDREN_IMG = "https://images.unsplash.com/photo-1622294891694-07a7ebbd9f37?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const ADULT_IMG = "https://images.unsplash.com/photo-1612299273045-362a39972259?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const FAMILY_IMG = "https://images.unsplash.com/photo-1649972904349-6e44c42644a7?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";
const TEACHER_IMG = "https://images.unsplash.com/photo-1531123897727-8f129e1688ce?crop=entropy&cs=srgb&fm=jpg&q=85&w=800";

export default function Landing() {
  const [meta, setMeta] = useState({ waiting_list_count: 0, waiting_list_goal: 200, launch_date: "2026-08-01T10:00:00+01:00" });
  const waitingRef = useRef(null);
  const trialRef = useRef(null);

  useEffect(() => {
    api.get("/meta").then((res) => setMeta(res.data)).catch(() => {});
  }, []);

  const scrollTo = (ref) => {
    ref?.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const goWaiting = () => scrollTo(waitingRef);
  const goTrial = () => scrollTo(trialRef);

  const progressPct = Math.min(100, Math.round(((meta.waiting_list_count + 1) / (meta.waiting_list_goal || 200)) * 100));

  return (
    <div id="top" className="min-h-screen bg-background">
      <Nav onJoinClick={goWaiting} whatsappNumber={WHATSAPP_NUMBER} />

      {/* HERO */}
      <section className="relative overflow-hidden pt-28 md:pt-32">
        <div className="pattern-adire pointer-events-none absolute inset-0" />
        <div className="mx-auto grid max-w-7xl gap-12 px-4 pb-16 sm:px-6 md:grid-cols-12 md:pb-24 lg:px-8">
          <div className="md:col-span-7 fade-up">
            <Badge
              variant="secondary"
              className="mb-6 rounded-full bg-[hsl(41_85%_55%)]/15 px-4 py-1.5 text-[hsl(15_60%_35%)] hover:bg-[hsl(41_85%_55%)]/20"
              data-testid="hero-badge"
            >
              Launching Saturday, 1 August 2026 · UK
            </Badge>
            <h1
              className="font-serif text-[clamp(2.5rem,6vw,4.5rem)] font-medium leading-[1.05] tracking-tight text-primary"
              data-testid="hero-heading"
            >
              Give your children the gift of their{" "}
              <span className="text-[hsl(15_60%_45%)] italic">mother tongue.</span>
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-muted-foreground">
              <strong className="font-semibold text-foreground">
                Live online Hausa classes for children and adults across the UK.
              </strong>{" "}
              Interactive weekly lessons taught by qualified native teachers
              from Nigeria — building confidence, culture and connection.
            </p>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Button
                onClick={goWaiting}
                data-testid="hero-join-waiting-list"
                className="rounded-full bg-primary px-8 py-6 text-base font-medium text-primary-foreground shadow-md transition-transform hover:-translate-y-0.5 hover:bg-primary/90"
              >
                Join the Waiting List
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button
                onClick={goTrial}
                variant="outline"
                data-testid="hero-book-trial"
                className="rounded-full border-primary/30 px-8 py-6 text-base font-medium text-primary hover:bg-primary/5"
              >
                Book Your Free Trial
              </Button>
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
              {["Weekly live classes", "Google Meet", "Recordings included", "Ages 5+", "Adults welcome"].map((t) => (
                <div key={t} className="inline-flex items-center gap-2">
                  <Check className="h-4 w-4 text-primary" /> {t}
                </div>
              ))}
            </div>
          </div>

          <div className="md:col-span-5 fade-up">
            <div className="relative">
              <div className="absolute -left-6 -top-6 hidden h-40 w-40 rounded-full bg-[hsl(41_85%_55%)]/20 blur-2xl md:block" />
              <img
                src={HERO_IMG}
                alt="Nigerian family laughing together at home"
                className="relative aspect-[4/5] w-full rounded-[2rem] object-cover shadow-2xl shadow-primary/10"
              />
              <div className="absolute -bottom-6 -left-4 hidden max-w-[220px] rounded-2xl border border-border bg-white p-4 shadow-lg md:block">
                <div className="text-xs uppercase tracking-widest text-muted-foreground">
                  Launching in
                </div>
                <CountdownTimer targetIso={meta.launch_date} compact />
              </div>
            </div>
          </div>
        </div>

        {/* Progress + Countdown strip */}
        <div className="mx-auto -mt-6 max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-4 rounded-3xl border border-border bg-card p-6 shadow-sm md:grid-cols-2 md:p-8">
            <div>
              <div className="mb-2 flex items-center justify-between">
                <div className="text-sm font-medium uppercase tracking-widest text-muted-foreground">
                  Waiting list
                </div>
                <div className="text-sm text-primary">
                  <span data-testid="waiting-count" className="font-serif text-lg">
                    {meta.waiting_list_count}
                  </span>{" "}
                  / {meta.waiting_list_goal} families
                </div>
              </div>
              <Progress value={progressPct} className="h-3 rounded-full" data-testid="waiting-progress" />
              <p className="mt-3 text-sm text-muted-foreground">
                Places are filling. Founding members get first pick of class times.
              </p>
            </div>
            <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-center">
              <div>
                <div className="text-sm font-medium uppercase tracking-widest text-muted-foreground">
                  Countdown to launch
                </div>
                <div className="font-serif text-xl text-primary">Saturday, 1 August 2026</div>
              </div>
              <CountdownTimer targetIso={meta.launch_date} />
            </div>
          </div>
        </div>
      </section>

      {/* TRUST BAND */}
      <section className="mt-16 border-y border-border bg-muted/60">
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 gap-6 md:grid-cols-6">
            {TRUST.map((t) => (
              <div
                key={t.label}
                className="flex flex-col items-center gap-2 text-center"
                data-testid={`trust-${t.label.replace(/\s+/g, "-").toLowerCase()}`}
              >
                <t.icon className="h-6 w-6 text-primary" strokeWidth={1.5} />
                <div className="text-xs font-medium text-foreground/80 md:text-sm">
                  {t.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY US / COMMUNITY FIRST */}
      <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8">
        <div className="grid gap-12 md:grid-cols-12 md:gap-16">
          <div className="md:col-span-5">
            <div className="text-sm uppercase tracking-[0.25em] text-[hsl(15_60%_45%)]">Why us</div>
            <h2 className="heading-flourish mt-3 text-4xl leading-tight md:text-5xl">
              More than a language class —<br /> a family investing in its heritage.
            </h2>
            <p className="mt-6 text-lg leading-relaxed text-muted-foreground">
              At AfroLingo your monthly membership joins you to
              a growing community of Nigerian diaspora families keeping Hausa
              alive at home. Children don't just learn vocabulary — they build
              the confidence to speak with grandparents, cousins and their
              wider community.
            </p>
            <div className="mt-8 flex gap-4">
              <Button
                onClick={goWaiting}
                data-testid="whyus-cta"
                className="rounded-full bg-primary px-8 py-6 text-base text-primary-foreground hover:bg-primary/90"
              >
                Reserve your family's spot
              </Button>
            </div>
          </div>

          <div className="md:col-span-7">
            <div className="grid grid-cols-2 gap-4 md:gap-6">
              <div className="col-span-2 overflow-hidden rounded-3xl border border-border">
                <img src={FAMILY_IMG} alt="Family learning together at home" className="h-64 w-full object-cover md:h-80" />
              </div>
              <Card icon={HeartHandshake} title="Community first" text="Not a tutoring transaction — a shared cultural journey." />
              <Card icon={GraduationCap} title="Native teachers" text="Qualified, warm, patient — trained to teach diaspora kids." />
              <Card icon={Globe2} title="Rooted, everywhere" text="Learn from anywhere in the UK on any device." />
              <Card icon={BookOpenCheck} title="Real progress" text="12-week roadmap, weekly homework, tracked milestones." />
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="bg-muted/60 py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <div className="text-sm uppercase tracking-[0.25em] text-[hsl(15_60%_45%)]">How classes work</div>
            <h2 className="mt-3 text-4xl md:text-5xl">Three simple steps to your first lesson.</h2>
          </div>

          <div className="mt-14 grid gap-6 md:grid-cols-3 md:gap-8">
            {[
              { n: "01", t: "Register online", d: "Join the waiting list or book a free trial. Takes 60 seconds." },
              { n: "02", t: "Receive your Meet link", d: "We email your Google Meet invitation and welcome pack." },
              { n: "03", t: "Learn live, weekly", d: "Join Saturday classes. Miss one? Recordings are yours to keep." },
            ].map((s) => (
              <div
                key={s.n}
                className="group relative overflow-hidden rounded-3xl border border-border bg-card p-8 shadow-sm transition-all hover:-translate-y-1 hover:shadow-xl hover:shadow-primary/10"
                data-testid={`how-step-${s.n}`}
              >
                <div className="mb-6 font-serif text-6xl text-[hsl(41_85%_55%)]">{s.n}</div>
                <h3 className="text-2xl">{s.t}</h3>
                <p className="mt-3 text-muted-foreground">{s.d}</p>
              </div>
            ))}
          </div>

          <div className="mt-10 text-center">
            <Button
              onClick={goTrial}
              variant="outline"
              className="rounded-full border-primary/30 px-8 py-6 text-base text-primary hover:bg-primary/5"
              data-testid="how-cta"
            >
              Book Your Free Trial
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* COURSES + SCHEDULE */}
      <section id="courses" className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8">
        <div className="grid gap-12 md:grid-cols-12">
          <div className="md:col-span-5">
            <div className="text-sm uppercase tracking-[0.25em] text-[hsl(15_60%_45%)]">Beginners Hausa</div>
            <h2 className="heading-flourish mt-3 text-4xl md:text-5xl">
              From "sannu" to full conversation.
            </h2>
            <p className="mt-6 text-lg text-muted-foreground">
              A structured beginner course designed for total newcomers. Each
              class blends teaching with games, songs, stories and speaking
              practice — so kids stay excited and adults stay engaged.
            </p>
            <ul className="mt-6 grid grid-cols-2 gap-3 text-sm">
              {["Alphabet & sounds","Greetings","Numbers","Family","Colours","Everyday phrases","Culture & songs","Speaking practice","Homework","Digital workbook"].map((x) => (
                <li key={x} className="flex items-center gap-2 text-foreground/85">
                  <Check className="h-4 w-4 text-primary" /> {x}
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-7">
            <Tabs defaultValue="children-junior" className="w-full">
              <TabsList className="grid w-full grid-cols-3 rounded-full bg-muted p-1">
                <TabsTrigger value="children-junior" className="rounded-full" data-testid="tab-junior">Ages 5–8</TabsTrigger>
                <TabsTrigger value="children-senior" className="rounded-full" data-testid="tab-senior">Ages 9–13</TabsTrigger>
                <TabsTrigger value="adults" className="rounded-full" data-testid="tab-adults">Adults</TabsTrigger>
              </TabsList>

              <TabsContent value="children-junior" className="mt-6">
                <ScheduleCard row={SCHEDULE[0]} img={CHILDREN_IMG} note="Playful, song-based lessons with lots of movement and repetition." />
              </TabsContent>
              <TabsContent value="children-senior" className="mt-6">
                <ScheduleCard row={SCHEDULE[1]} img={CHILDREN_IMG} note="Confidence in reading, conversation and cultural understanding." />
              </TabsContent>
              <TabsContent value="adults" className="mt-6">
                <ScheduleCard row={SCHEDULE[2]} img={ADULT_IMG} note="Adult-paced learning — perfect for parents wanting to catch up alongside their kids." />
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="bg-muted/60 py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <div className="text-sm uppercase tracking-[0.25em] text-[hsl(15_60%_45%)]">Membership</div>
            <h2 className="mt-3 text-4xl md:text-5xl">
              One price. One community. Every language you'll ever want.
            </h2>
          </div>

          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {/* Trial */}
            <div className="rounded-3xl border border-border bg-card p-8 shadow-sm">
              <div className="text-sm uppercase tracking-widest text-muted-foreground">Free Trial</div>
              <div className="mt-4 font-serif text-5xl text-primary">£0</div>
              <p className="mt-2 text-muted-foreground">Your first live class, completely free.</p>
              <ul className="mt-6 space-y-3 text-sm">
                <li className="flex gap-2"><Check className="h-4 w-4 text-primary mt-1" /> One live lesson</li>
                <li className="flex gap-2"><Check className="h-4 w-4 text-primary mt-1" /> Meet the teacher</li>
                <li className="flex gap-2"><Check className="h-4 w-4 text-primary mt-1" /> No card required</li>
              </ul>
              <Button
                onClick={goTrial}
                variant="outline"
                data-testid="pricing-trial-cta"
                className="mt-8 w-full rounded-full border-primary/30 py-6 text-base text-primary hover:bg-primary/5"
              >
                Book Free Class
              </Button>
            </div>

            {/* Monthly */}
            <div className="relative rounded-3xl border-2 border-primary bg-card p-8 shadow-xl shadow-primary/10">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-[hsl(41_85%_55%)] px-4 py-1 text-xs font-semibold uppercase tracking-widest text-[hsl(150_20%_15%)]">
                Most popular
              </div>
              <div className="text-sm uppercase tracking-widest text-muted-foreground">Monthly Membership</div>
              <div className="mt-4 flex items-end gap-1">
                <span className="font-serif text-5xl text-primary">£20</span>
                <span className="mb-1.5 text-muted-foreground">/ student / month</span>
              </div>
              <p className="mt-2 text-muted-foreground">Everything your family needs to learn together.</p>
              <ul className="mt-6 space-y-3 text-sm">
                <li className="flex gap-2"><Check className="h-4 w-4 text-primary mt-1" /> 4 live Saturday classes</li>
                <li className="flex gap-2"><Check className="h-4 w-4 text-primary mt-1" /> All class recordings</li>
                <li className="flex gap-2"><Check className="h-4 w-4 text-primary mt-1" /> Weekly digital workbook</li>
                <li className="flex gap-2"><Check className="h-4 w-4 text-primary mt-1" /> Homework & progress tracking</li>
                <li className="flex gap-2"><Check className="h-4 w-4 text-primary mt-1" /> Community access</li>
              </ul>
              <Button
                onClick={goWaiting}
                data-testid="pricing-monthly-cta"
                className="mt-8 w-full rounded-full bg-primary py-6 text-base text-primary-foreground hover:bg-primary/90"
              >
                Join Waiting List
              </Button>
            </div>

            {/* Family */}
            <div className="rounded-3xl border border-border bg-card p-8 shadow-sm">
              <div className="text-sm uppercase tracking-widest text-muted-foreground">Family Offer</div>
              <div className="mt-4 font-serif text-5xl text-primary">−30%</div>
              <p className="mt-2 text-muted-foreground">For siblings learning together.</p>
              <ul className="mt-6 space-y-3 text-sm">
                <li className="flex gap-2"><Check className="h-4 w-4 text-primary mt-1" /> Automatic sibling discount</li>
                <li className="flex gap-2"><Check className="h-4 w-4 text-primary mt-1" /> Same class or different ages</li>
                <li className="flex gap-2"><Check className="h-4 w-4 text-primary mt-1" /> Cancel anytime</li>
              </ul>
              <Button
                onClick={goWaiting}
                variant="outline"
                data-testid="pricing-family-cta"
                className="mt-8 w-full rounded-full border-primary/30 py-6 text-base text-primary hover:bg-primary/5"
              >
                Register siblings
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* MEET THE TEACHER */}
      <section id="teacher" className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8">
        <div className="grid gap-12 md:grid-cols-12 md:items-center">
          <div className="md:col-span-5">
            <div className="relative">
              <img
                src={TEACHER_IMG}
                alt="Ameenah — your native Hausa teacher"
                className="aspect-square w-full rounded-[2rem] object-cover shadow-xl shadow-primary/10"
              />
              <div className="absolute -bottom-5 left-6 rounded-2xl bg-[hsl(41_85%_55%)] px-4 py-2 font-serif text-[hsl(150_20%_15%)]">
                Ameenah · Native Hausa teacher · Nigeria
              </div>
            </div>
          </div>
          <div className="md:col-span-7">
            <div className="text-sm uppercase tracking-[0.25em] text-[hsl(15_60%_45%)]">Meet the teacher</div>
            <h2 className="heading-flourish mt-3 text-4xl md:text-5xl">
              Meet Ameenah — warm, qualified and passionate about preserving language.
            </h2>
            <p className="mt-6 text-lg leading-relaxed text-muted-foreground">
              Ameenah is a qualified native Hausa speaker from northern Nigeria
              with years of experience teaching both children and adults
              online. She blends traditional cultural storytelling with a
              modern, interactive teaching approach — designed specifically
              for diaspora learners.
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              {["Native speaker","Qualified teacher","Children specialist","Patient & warm","Culture keeper"].map((t) => (
                <Badge key={t} variant="secondary" className="rounded-full bg-muted px-4 py-1.5 text-foreground/80 hover:bg-muted">
                  {t}
                </Badge>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CURRICULUM — three learning blocks */}
      <section className="bg-muted/60 py-24">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="text-sm uppercase tracking-[0.25em] text-[hsl(15_60%_45%)]">
              12-week beginner curriculum
            </div>
            <h2 className="mt-3 text-4xl md:text-5xl">
              Three learning blocks. Measurable progress.
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-muted-foreground">
              Every block ends with a progress assessment so parents can see
              real confidence being built — not just lessons attended.
            </p>
          </div>

          <div className="mt-14 space-y-10" data-testid="curriculum-blocks">
            {CURRICULUM_BLOCKS.map((block, bi) => (
              <div
                key={block.id}
                data-testid={`curriculum-${block.id}`}
                className="overflow-hidden rounded-3xl border border-border bg-card shadow-sm"
              >
                <div className="flex flex-col gap-2 border-b border-border/70 bg-[hsl(148_40%_96%)] px-6 py-6 md:flex-row md:items-center md:justify-between md:px-8">
                  <div className="flex items-start gap-4">
                    <div className="grid h-12 w-12 flex-none place-items-center rounded-2xl bg-primary text-primary-foreground font-serif text-xl">
                      {bi + 1}
                    </div>
                    <div>
                      <div className="text-[11px] uppercase tracking-[0.25em] text-[hsl(15_60%_45%)]">
                        Learning Block {bi + 1}
                      </div>
                      <h3 className="mt-1 text-2xl leading-tight">{block.title}</h3>
                      <p className="mt-1 text-sm text-muted-foreground">{block.subtitle}</p>
                    </div>
                  </div>
                </div>

                <Accordion
                  type="single"
                  collapsible
                  className="w-full"
                  data-testid={`curriculum-accordion-${block.id}`}
                  defaultValue={bi === 0 ? `${block.id}-0` : undefined}
                >
                  {block.weeks.map((w, wi) => (
                    <AccordionItem
                      key={w.week}
                      value={`${block.id}-${wi}`}
                      className="px-6 md:px-8"
                    >
                      <AccordionTrigger className="text-left">
                        <div className="flex flex-1 items-center gap-4 pr-4">
                          <span className="font-serif text-lg text-[hsl(15_60%_45%)]">
                            {w.week}
                          </span>
                          <span className="flex-1 text-base font-normal">
                            {w.title}
                          </span>
                          {w.assessment && (
                            <Badge
                              className="rounded-full bg-[hsl(41_85%_55%)] px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-[hsl(150_20%_15%)] hover:bg-[hsl(41_85%_55%)]"
                            >
                              Assessment
                            </Badge>
                          )}
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="text-muted-foreground">
                        <div className="mb-2 text-xs font-medium uppercase tracking-widest text-foreground/70">
                          Theme · {w.theme}
                        </div>
                        <p className="leading-relaxed">{w.detail}</p>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            ))}
          </div>

          <div className="mt-14 rounded-3xl border border-border bg-card p-8 md:p-10">
            <div className="text-sm uppercase tracking-[0.25em] text-[hsl(15_60%_45%)]">
              Why this structure works
            </div>
            <h3 className="mt-3 text-2xl md:text-3xl">
              A rhythm parents already trust — with clear milestones.
            </h3>
            <div className="mt-8 grid gap-4 md:grid-cols-3">
              {[
                { label: "Weeks 1–3", text: "Learn new foundational skills" },
                { label: "Week 4", text: "Progress Assessment 1 · feedback to parents" },
                { label: "Weeks 5–7", text: "Everyday vocabulary" },
                { label: "Week 8", text: "Progress Assessment 2 · practical review" },
                { label: "Weeks 9–11", text: "Build conversational confidence" },
                { label: "Week 12", text: "Final assessment · certificate · showcase" },
              ].map((row) => (
                <div
                  key={row.label}
                  className="rounded-2xl border border-border bg-muted/50 p-5"
                >
                  <div className="font-serif text-lg text-primary">{row.label}</div>
                  <div className="mt-1 text-sm text-muted-foreground">{row.text}</div>
                </div>
              ))}
            </div>
            <p className="mt-6 text-sm text-muted-foreground">
              This structure mirrors the way schools assess progress, provides
              families clear milestones to celebrate, and encourages students to
              continue into the next term with confidence.
            </p>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS PLACEHOLDER */}
      <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <div className="text-sm uppercase tracking-[0.25em] text-[hsl(15_60%_45%)]">Families are saying</div>
          <h2 className="mt-3 text-4xl md:text-5xl">Early stories from our founding families.</h2>
        </div>

        <div className="mt-14 grid gap-6 md:grid-cols-3">
          {[
            { name: "Amina & family", place: "Manchester", quote: "My daughter now greets her grandma in Hausa every Sunday. Priceless." },
            { name: "The Bello family", place: "London", quote: "Both my boys look forward to Saturday mornings. That's a miracle." },
            { name: "Zainab, adult learner", place: "Birmingham", quote: "I never had the chance growing up. Finally learning the language of my parents feels emotional." },
          ].map((r) => (
            <div key={r.name} className="rounded-3xl border border-border bg-card p-8 shadow-sm">
              <Quote className="h-8 w-8 text-[hsl(41_85%_55%)]" strokeWidth={1.5} />
              <p className="mt-4 text-lg leading-relaxed text-foreground/85">"{r.quote}"</p>
              <div className="mt-6 flex items-center justify-between">
                <div>
                  <div className="font-medium">{r.name}</div>
                  <div className="text-sm text-muted-foreground">{r.place}</div>
                </div>
                <div className="flex text-[hsl(41_85%_55%)]">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-current" />
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* WAITING LIST FORM */}
      <section
        ref={waitingRef}
        id="waitinglist"
        className="scroll-mt-24 bg-primary py-24 text-primary-foreground grain"
        data-testid="waiting-list-section"
      >
        <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 md:grid-cols-12 lg:px-8">
          <div className="md:col-span-5">
            <div className="text-sm uppercase tracking-[0.25em] text-[hsl(41_85%_75%)]">Waiting list</div>
            <h2 className="mt-3 text-4xl leading-tight md:text-5xl">
              Reserve your family's place — before founding memberships fill.
            </h2>
            <p className="mt-6 text-lg text-primary-foreground/85">
              We open only a limited number of founding memberships for our
              first cohort. Founders get first-pick class times and locked-in
              pricing for life.
            </p>
            <ul className="mt-8 space-y-3 text-primary-foreground/90">
              <li className="flex gap-2"><Check className="h-5 w-5 text-[hsl(41_85%_75%)]" /> Priority class selection</li>
              <li className="flex gap-2"><Check className="h-5 w-5 text-[hsl(41_85%_75%)]" /> Founding member pricing</li>
              <li className="flex gap-2"><Check className="h-5 w-5 text-[hsl(41_85%_75%)]" /> Early access to Yoruba & Igbo</li>
              <li className="flex gap-2"><Check className="h-5 w-5 text-[hsl(41_85%_75%)]" /> Free trial class included</li>
            </ul>
          </div>
          <div className="md:col-span-7">
            <div className="rounded-3xl bg-white p-6 text-foreground shadow-2xl md:p-10">
              <WaitingListForm />
            </div>
          </div>
        </div>
      </section>

      {/* FREE TRIAL FORM */}
      <section
        ref={trialRef}
        id="trial"
        className="scroll-mt-24 bg-muted/60 py-24"
        data-testid="trial-section"
      >
        <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 md:grid-cols-12 lg:px-8">
          <div className="md:col-span-5">
            <div className="text-sm uppercase tracking-[0.25em] text-[hsl(15_60%_45%)]">Free trial</div>
            <h2 className="heading-flourish mt-3 text-4xl leading-tight md:text-5xl">
              Try one class. On us.
            </h2>
            <p className="mt-6 text-lg text-muted-foreground">
              Not sure yet? Book our free introductory Hausa class. Meet the
              teacher, feel the community energy and see your child light up.
            </p>
            <div className="mt-6 rounded-3xl border border-border bg-card p-6">
              <div className="flex items-center gap-4">
                <div className="grid h-12 w-12 place-items-center rounded-full bg-[hsl(41_85%_55%)]/20">
                  <Video className="h-6 w-6 text-primary" strokeWidth={1.5} />
                </div>
                <div>
                  <div className="font-medium">1 free live class</div>
                  <div className="text-sm text-muted-foreground">No card. No commitment.</div>
                </div>
              </div>
            </div>
          </div>
          <div className="md:col-span-7">
            <div className="rounded-3xl bg-white p-6 shadow-xl shadow-primary/5 md:p-10">
              <TrialForm />
            </div>
          </div>
        </div>
      </section>

      {/* COMMUNITY / FUTURE */}
      <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8">
        <div className="grid gap-10 md:grid-cols-12 md:items-center">
          <div className="md:col-span-6">
            <div className="text-sm uppercase tracking-[0.25em] text-[hsl(15_60%_45%)]">Community</div>
            <h2 className="heading-flourish mt-3 text-4xl md:text-5xl">
              Not a class. A movement.
            </h2>
            <p className="mt-6 text-lg text-muted-foreground">
              AfroLingo families become part of a growing diaspora community
              preserving language, culture and identity. As the community
              grows, so does the curriculum — with new languages, holiday
              programmes and conversation clubs.
            </p>
            <div className="mt-8 flex flex-wrap gap-2">
              {["Hausa (live)","Yoruba (coming)","Igbo (coming)","Fulfulde","Kanuri","Swahili","Holiday programmes","Conversation clubs"].map((t) => (
                <Badge
                  key={t}
                  variant="secondary"
                  className={`rounded-full px-4 py-1.5 ${t.includes("live") ? "bg-[hsl(148_50%_35%)] text-white hover:bg-[hsl(148_50%_30%)]" : "bg-muted text-foreground/80 hover:bg-muted"}`}
                >
                  {t}
                </Badge>
              ))}
            </div>
          </div>
          <div className="md:col-span-6">
            <img
              src={ADULT_IMG}
              alt="Diaspora adult learning online"
              className="w-full rounded-[2rem] object-cover shadow-xl shadow-primary/10"
            />
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="bg-muted/60 py-24">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="text-sm uppercase tracking-[0.25em] text-[hsl(15_60%_45%)]">FAQ</div>
            <h2 className="mt-3 text-4xl md:text-5xl">Everything a curious parent asks.</h2>
          </div>
          <div className="mt-12 rounded-3xl border border-border bg-card p-2 md:p-4">
            <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
              {FAQS.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="px-4">
                  <AccordionTrigger className="text-left text-base md:text-lg">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>

          <div className="mt-12 text-center">
            <Button
              onClick={goWaiting}
              data-testid="faq-cta"
              className="rounded-full bg-primary px-8 py-6 text-base text-primary-foreground hover:bg-primary/90"
            >
              Join the Waiting List
            </Button>
          </div>
        </div>
      </section>

      <Footer email={CONTACT_EMAIL} whatsapp={WHATSAPP_NUMBER} />
      <StickyCTA onJoinClick={goWaiting} whatsappNumber={WHATSAPP_NUMBER} />
    </div>
  );
}

function Card({ icon: Icon, title, text }) {
  return (
    <div className="rounded-3xl border border-border bg-card p-6 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-lg">
      <Icon className="h-6 w-6 text-primary" strokeWidth={1.5} />
      <h4 className="mt-4 text-lg">{title}</h4>
      <p className="mt-2 text-sm text-muted-foreground">{text}</p>
    </div>
  );
}

function ScheduleCard({ row, img, note }) {
  return (
    <div className="grid gap-6 rounded-3xl border border-border bg-card p-6 md:grid-cols-2">
      <img src={img} alt={row.group} className="h-full min-h-[220px] w-full rounded-2xl object-cover" />
      <div className="flex flex-col justify-between">
        <div>
          <h4 className="text-2xl">{row.group}</h4>
          <p className="mt-2 text-muted-foreground">{note}</p>
        </div>
        <div className="mt-6 grid grid-cols-2 gap-3">
          <div className="rounded-2xl p-4" style={{ background: row.tone }}>
            <div className="text-xs uppercase tracking-widest text-foreground/70">Day</div>
            <div className="font-serif text-xl text-primary">{row.day}</div>
          </div>
          <div className="rounded-2xl p-4" style={{ background: row.tone }}>
            <div className="text-xs uppercase tracking-widest text-foreground/70">Time</div>
            <div className="font-serif text-xl text-primary">{row.time}</div>
          </div>
          <div className="rounded-2xl p-4" style={{ background: row.tone }}>
            <div className="text-xs uppercase tracking-widest text-foreground/70">Duration</div>
            <div className="font-serif text-xl text-primary">1 hour</div>
          </div>
          <div className="rounded-2xl p-4" style={{ background: row.tone }}>
            <div className="text-xs uppercase tracking-widest text-foreground/70">Platform</div>
            <div className="font-serif text-xl text-primary">Google Meet</div>
          </div>
        </div>
      </div>
    </div>
  );
}
